import { themeColors } from "../../themeColors";
import BackButton from "@/components/Common/BackButton";
/**
 * @file   src\components\PlanUpgrade\PremiumPlanModal.js
 * @brief  This file is responsible for Plan upgrade section for agents
 * @date   July, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { Button, Tab, Tabs } from "react-bootstrap";
import CancelPlanModal from "./CancelPlanModal";
import PlanReviewModalWrapper from "../Payments/StripeElementsWrapper";
import {
  getLocalStorage,
  setLocalStorage,
  getUserType,
  checkPlanCanbeUpgraded,
} from "../../helpers/common";
import { toast } from "react-toastify";
import {
  createSubscriptionPlan,
  createSubscriptionPlanOnSignup,
  getActivePromoCodeDetails,
} from "../../services/PlanService";
import {
  AGENT_USER_FREE_PLAN,
  USER_INFO_KEY,
  USER_TYPE_MEMBER,
} from "../../constants/common";
import { useSelector } from "react-redux";
import ContactModal from "../Onboarding/ContactModal";
import PlanUpgradeRestrictionModal from "./PlanUpgradeRestrictionModal";
import ShowEditProfileModal from "./ShowEditProfileModal";
import { pageURLs } from "@/constants";
import { useNavigate } from "react-router-dom";
import useIsMobile from "@/hooks/useIsMobile";
import { useAppConfig } from "@/appConfig";
import { isMlsRestrictedTenant } from "@/helpers/tenant";
import GitchaLogo from "../../images/gitcha-logo.svg";
import {
  blsProDescription,
  BLS_PRO_FEATURES,
  BLS_PRO_COMING_SOON,
  BLS_PRO_PRICING,
} from "@/constants/blsPlan";

const PremiumPlanModal = (props) => {
  const {
    userDetails,
    updateReloadParam,
    handleShowAgentModal,
    show,
    handleAgentTitleModal,
    UpgradeToAgent,
  } = props;
  const [showPlanReviewModal, setPlanReviewModal] = useState(false);
  const PlanReviewModalClose = () => setPlanReviewModal(false);
  const PlanReviewModalShow = () => setPlanReviewModal(true);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [key, setKey] = useState("annual");
  const [fromSignUp, setFromSignUp] = useState(false);
  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );
  const [showCancelPlanModal, setCancelPlanModal] = useState(false);
  const CancelPlanModalClose = () => setCancelPlanModal(false);
  const CancelPlanModalShow = () => setCancelPlanModal(true);
  const userType = getUserType();
  const [currentActivePlan, setCurrentActivePlan] = useState(null);
  // contact modal
  const [contactModalState, setContactModalState] = useState(false);
  const ContactModalClose = () => setContactModalState(false);
  const ContactModalShow = () => setContactModalState(true);
  const [enablePromocode, setEnablePromocode] = useState(false);
  const [enablePromocodePeriod, setEnablePromoCodePeriod] = useState([]);
  const [showPlanRestrictionModal, setShowPlanRestrictionModal] =
    useState(false);
  const [continueButtonClicked, setContinueButtonClicked] = useState(false);

  const [editProfileModal, setEditProfileModal] = useState(false);
  const EditProfileModalClose = () => setEditProfileModal(false);
  const EditProfileModalShow = () => setEditProfileModal(true);

  const navigate = useNavigate();

  useEffect(() => {
    if (show) {
      getActivePromoCodeDetails().then((response) => {
        let temp = [];
        const res = _.get(response, "result", []);
        if (res?.length > 0) {
          const result = res.filter(
            (planDetails) =>
              planDetails?.UserType == 2 || planDetails?.UserType == 3,
          );
          if (result && result.length > 0) {
            setEnablePromocode(true);
          }
          result.forEach((data) => {
            temp.push(data?.Period);
          });
          setEnablePromoCodePeriod(temp);
        }
      });
    }
  }, [show]);

  useEffect(() => {
    if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
      const planCurrent = userDetailsFromStore.filter(
        (pl) => pl?.Is_Upcoming === 0 && pl?.IsActive === 1,
      );
      setCurrentActivePlan(planCurrent[0]);
    }
  }, [userDetailsFromStore]);

  useEffect(() => {
    if (userDetails?.PlanName == null) {
      setFromSignUp(true);
    } else {
      setFromSignUp(false);
    }
    setSelectedPeriod(userDetails?.period);
    setSelectedPlan(userDetails?.PlanName);
  }, [userDetails]);

  const onSignupPremiumAgentSuccess = () => {
    PlanReviewModalClose();
    props.onHide();
    navigate(pageURLs.completeYourProfileDefault);
  };

  const handleUpgrade = (period, plan, continueClicked = false) => {
    setSelectedPeriod(period);
    setSelectedPlan(plan);
    if (!continueClicked) {
      setContinueButtonClicked(false);
    }
    if (
      checkPlanCanbeUpgraded(currentActivePlan, plan, period)?.canPlanUpgrade ==
        false &&
      !continueClicked
    ) {
      setShowPlanRestrictionModal(true);
    } else {
      PlanReviewModalShow();
    }
    props.onHide();
  };

  useEffect(() => {
    if (selectedPeriod === 3) {
      setKey("annual");
    } else if (selectedPeriod === 2) {
      setKey("monthly");
    }
  }, [selectedPeriod]);

  const handleDowngrade = () => {
    props.onHide();
    CancelPlanModalShow();
  };

  const handleCancel = () => {
    if (userDetails?.period === 2) {
      setKey("monthly");
    } else {
      setKey("annual");
    }
    props.onHide();
  };

  const upgradeUserToFreePlan = async (continueClicked = false) => {
    setSelectedPeriod(1);
    setSelectedPlan("Lite");
    if (!continueClicked) {
      setContinueButtonClicked(false);
    }
    if (
      userType === USER_TYPE_MEMBER &&
      checkPlanCanbeUpgraded(currentActivePlan, "Lite", 1)?.canPlanUpgrade ==
        false &&
      !continueClicked
    ) {
      setShowPlanRestrictionModal(true);
      props.onHide();
    } else {
      const data = {
        PlanName: "Lite",
        PlanPeriod: 1,
        UserType: userType,
        PopupStatus: continueClicked ? true : false,
      };

      if (UpgradeToAgent === true) {
        data.changeUserType = 2;
        await createSubscriptionPlan(data)
          .then(async (response) => {
            if (response.errorCode === 0) {
              props.onHide();
              toast.success("You've selected Lite!");
              if (UpgradeToAgent === true) {
                EditProfileModalShow();
              } else {
                if (props.updateReloadParam) props.updateReloadParam();
              }
            } else {
              toast.error(response.message);
            }
          })
          .catch((error) => {
            toast.error(error);
          });
      } else {
        await createSubscriptionPlanOnSignup(data)
          .then(async (response) => {
            if (response.errorCode === 0) {
              props.onHide();
              if (props.updateReloadParam) props.updateReloadParam();
              toast.dismiss();
              toast.success("You've selected Lite!");
              const userInfo = getLocalStorage(USER_INFO_KEY);
              userInfo.userInfo["planStatus"] = true;
              setLocalStorage(USER_INFO_KEY, userInfo);
              navigate(pageURLs.completeYourProfileDefault);
            } else {
              toast.error(response.message);
            }
          })
          .catch((error) => {
            toast.error(error);
          });
      }
    }
  };

  const handleContinueClick = () => {
    setContinueButtonClicked(true);
    if (selectedPlan === AGENT_USER_FREE_PLAN) {
      upgradeUserToFreePlan(true);
    } else {
      handleUpgrade(selectedPeriod, selectedPlan, true);
    }
  };

  const isMobile = useIsMobile();
  const { tenant } = useAppConfig() || {};
  const isBLS = isMlsRestrictedTenant(tenant);
  const mlsShortName = tenant?.mlsShortName || "MLS";
  const proMemberInfo = isBLS
    ? blsProDescription(mlsShortName)
    : "Specific features for agents and teams to create and manage deals.";
  const blsProMemberFeatures = BLS_PRO_FEATURES;
  const blsComingSoonFeature = BLS_PRO_COMING_SOON;

  return (
    <>
      <Modal
        show
        {...props}
        aria-labelledby="contained-modal-title-vcenter"
        className={`modal-plan premium-plan-modal${isBLS ? " bls-upgrade-modal" : ""}`}
        backdrop="static"
        keyboard={!fromSignUp}
        fullscreen={isMobile}
      >
        <Modal.Body>
          {!fromSignUp && !props.fromInventoryCreation && (
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={handleCancel}
            ></button>
          )}
          {isBLS && !UpgradeToAgent ? (
            <>
              <div className="bls-upgrade-hero">
                <img
                  src={GitchaLogo}
                  alt="Gitcha"
                  className="bls-upgrade-hero__logo"
                />
                <h2 className="bls-upgrade-hero__headline">
                  Get found. Get chosen. Get clients.
                </h2>
                <p className="bls-upgrade-hero__subhead">
                  You're on Lite (free) today. Pro adds these tools to your{" "}
                  {mlsShortName} seat - no MLS subscription change.
                </p>
                <p className="bls-upgrade-hero__pitch">
                  Buyer agents don't get sign calls. Let's change that. Gitcha
                  Pro is how consumers, sellers, and referral agents find,
                  remember, and choose you - through your branded profile,
                  marketing tools, and lead&#8209;capture flows.
                </p>
              </div>

              <div className="bls-upgrade-solo">
                <div
                  className="bls-upgrade-solo__period"
                  role="radiogroup"
                  aria-label="Billing cycle"
                >
                  <label
                    className={`bls-upgrade-solo__period-option${key === "annual" ? " is-active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="bls-billing-period"
                      checked={key === "annual"}
                      onChange={() => setKey("annual")}
                    />
                    <span className="bls-upgrade-solo__period-inner">
                      <span className="bls-upgrade-solo__period-name">
                        Annual
                      </span>
                      <span className="bls-upgrade-solo__period-price">
                        ${BLS_PRO_PRICING.annual.perMo}<small>/mo</small>
                      </span>
                      <span className="bls-upgrade-solo__period-billed">
                        Billed annually (${BLS_PRO_PRICING.annual.perYr}/yr)
                      </span>
                    </span>
                  </label>
                  <label
                    className={`bls-upgrade-solo__period-option${key === "monthly" ? " is-active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="bls-billing-period"
                      checked={key === "monthly"}
                      onChange={() => setKey("monthly")}
                    />
                    <span className="bls-upgrade-solo__period-inner">
                      <span className="bls-upgrade-solo__period-name">
                        Monthly
                      </span>
                      <span className="bls-upgrade-solo__period-price">
                        ${BLS_PRO_PRICING.monthly.perMo}<small>/mo</small>
                      </span>
                      <span className="bls-upgrade-solo__period-billed">
                        Billed monthly
                      </span>
                    </span>
                  </label>
                </div>

                <p className="bls-upgrade-solo__eyebrow">
                  What you unlock as a Gitcha Pro
                </p>
                <ul className="bls-upgrade-solo__features">
                  {blsProMemberFeatures.map((f) => (
                    <li
                      key={f.label}
                      className="bls-upgrade-solo__feature"
                    >
                      <span
                        className="bls-upgrade-solo__check"
                        aria-hidden="true"
                      >
                        ✓
                      </span>
                      <div className="bls-upgrade-solo__feature-text">
                        <strong>{f.label}</strong>
                        <span>{f.benefit}</span>
                      </div>
                    </li>
                  ))}
                  <li className="bls-upgrade-solo__feature">
                    <span
                      className="bls-upgrade-solo__check"
                      aria-hidden="true"
                    >
                      ✓
                    </span>
                    <div className="bls-upgrade-solo__feature-text">
                      <strong>
                        {blsComingSoonFeature.label}{" "}
                        <span className="bls-upgrade-solo__cs-badge">
                          Coming Soon
                        </span>
                      </strong>
                      <span>{blsComingSoonFeature.benefit}</span>
                    </div>
                  </li>
                </ul>

                <Button
                  variant="primary"
                  className="bls-upgrade-solo__cta"
                  onClick={() =>
                    handleUpgrade(
                      key === "annual"
                        ? BLS_PRO_PRICING.annual.planId
                        : BLS_PRO_PRICING.monthly.planId,
                      "Pro Member",
                    )
                  }
                  disabled={userDetails?.PlanName === "Pro Member"}
                >
                  {userDetails?.PlanName === "Pro Member"
                    ? "You're already a Gitcha Pro"
                    : "Become a Gitcha Pro"}
                </Button>

                <p className="bls-upgrade-reassurance">
                  Cancel anytime. Your demand feed stays free forever.
                </p>
              </div>
            </>
          ) : (
            <>
              {UpgradeToAgent ? (
                <h3 className="text-center">Switch to an agent profile</h3>
              ) : userDetails?.PlanName == null ? (
                <h3 className="text-center">Select your plan</h3>
              ) : (
                <h3 className="text-center">
                  {props?.fromInventoryListing
                    ? "Upgrade to Premium to add more properties"
                    : "Upgrade your professional plan"}
                </h3>
              )}
              <p className="desc text-center">
                {UpgradeToAgent
                  ? "Select your plan to make your switch. Please be aware that if you currently have an investor profile, you’ll lose access to investor specific features & want criteria but gain agent specific features."
                  : props?.fromInventoryListing
                    ? "Your free plan is limited to 2 properties in your private inventory. Upgrade to our Premium plan to gain more that Gitcha has to offer."
                    : "Subscribe to save time and create more deals with unlimited access to all of Gitcha’s features."}{" "}
              </p>
            </>
          )}
          {(!isBLS || UpgradeToAgent) && (
          <div className="tab-wrap">
            <Tabs
              id="controlled-tab-example"
              activeKey={key}
              onSelect={(k) => setKey(k)}
            >
              <Tab eventKey="annual" title="Annual">
                <div className="plan-container">
                  <div className="plan-wraper member w-100">
                    <h4>Lite</h4>
                    <p className="info">
                      Get a taste of what Gitcha can do with limited features.
                    </p>
                    <p className="price">Free</p>
                    <div className="btn-sec">
                      {userDetails?.PlanName == null ||
                      userType === USER_TYPE_MEMBER ? (
                        <Button
                          variant="primary"
                          onClick={() => upgradeUserToFreePlan()}
                          className="plan-btn"
                        >
                          Select
                        </Button>
                      ) : userDetails?.PlanName === "Lite" ? (
                        <Button variant="primary" className="plan-btn" disabled>
                          Your current plan
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          className="btn-outline-primary plan-btn"
                          onClick={handleDowngrade}
                        >
                          Downgrade
                        </Button>
                      )}
                    </div>
                    {!isBLS && (
                      <ul className="plan-features">
                        <li>Unlimited want-listings</li>
                        <li>Up to 2 properties in your inventory</li>
                        <li>Pay per introduction</li>
                      </ul>
                    )}
                    {isBLS && (
                      <p className="bls-lite-baseline">
                        Included with your {mlsShortName} seat
                      </p>
                    )}
                  </div>
                  <div className="plan-wraper premium w-100 pad-lr-21">
                    {userDetails?.PlanName != "Pro Member" && (
                      <span className="table-badge-green">Most popular</span>
                    )}
                    <h4 className="d-flex align-items-center">
                      Pro Member <span className="pro-blue-badge">PRO</span>
                    </h4>
                    <p className="info">{proMemberInfo}</p>
                    <p className="price">
                      $12/month<span>Billed annually</span>
                    </p>
                    <div className="btn-sec">
                      {userDetails?.PlanName == null ? (
                        <Button
                          variant="primary"
                          className="plan-btn"
                          onClick={() => handleUpgrade(3, "Pro Member")}
                        >
                          Select
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          className="plan-btn"
                          disabled={
                            userDetails?.PlanName === "Pro Member"
                          }
                          onClick={() => handleUpgrade(3, "Pro Member")}
                        >
                          {userDetails?.PlanName === "Pro Member"
                            ? "Your Current Plan"
                            : isBLS
                              ? "Become a Gitcha Pro"
                              : "Upgrade to Pro Member"}
                        </Button>
                      )}
                    </div>
                    <ul className="plan-features">
                      {isBLS
                        ? blsProMemberFeatures.map((f) => (
                            <li key={f.label}>
                              <strong className="plan-features__label">
                                {f.label}
                              </strong>
                              <span className="plan-features__benefit">
                                {f.benefit}
                              </span>
                            </li>
                          ))
                        : (
                          <>
                            <li>Client management dashboard</li>
                            <li>Unlimited want-listings</li>
                            <li>Unlimited properties in your inventory</li>
                            <li>10 free introductions per month</li>
                            <li>Custom agent profile and Pro badge</li>
                            <li>Branded Buyer Digital Listing Form</li>
                            <li>See other Pro Member's contact info</li>
                          </>
                        )}
                    </ul>
                  </div>
                  {!isBLS && (
                    <div className="plan-wraper preferred w-100">
                      <h4 className="d-flex align-items-center">
                        Pro Preferred <span className="pro-gold-badge">PRO</span>
                      </h4>
                      <p className="info">
                        We’re working on new features that will be a game changer
                        for agents.
                      </p>
                      <p className="price">Coming soon</p>
                      <div className="btn-sec">
                        <Button
                          variant="outline"
                          className="btn-outline-blue plan-btn"
                        >
                          Notify Me
                        </Button>
                      </div>
                      <div className="plan-outer">
                        <ul className="plan-features">
                          <li>Unlimited want-listings</li>
                          <li>Custom agent profile and Premium badge</li>
                          <li>Unlimited properties in your inventory</li>
                          <li>10 free introductions per month</li>
                          <li>Client management dashboard </li>
                        </ul>
                      </div>
                    </div>
                  )}

                  {!isBLS && (
                    <div className="plan-wraper preferred w-100">
                      <h4>Enterprise</h4>
                      <p className="info">
                        Empowering your Agency's full team with Premium benefits.
                      </p>
                      <p className="price">Tiered pricing/seat</p>
                      <div className="btn-sec">
                        <Button
                          variant="primary"
                          className="plan-btn"
                          onClick={() => {
                            ContactModalShow();
                          }}
                        >
                          Contact us
                        </Button>
                      </div>
                      <ul className="plan-features">
                        <li>Unlimited want-listings</li>
                        <li>Custom agent profile and Premium badge</li>
                        <li>Unlimited properties in your inventory</li>
                        <li>Free introductions per month</li>
                        <li>Client management dashboard</li>
                        <li>
                          Exclusive market analytics, real-time buyer demand and
                          private inventory data reports
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </Tab>

              <Tab eventKey="monthly" title="Monthly">
                <div className="plan-container">
                  <div className="plan-wraper member w-100">
                    <h4>Lite</h4>
                    <p className="info">
                      Get a taste of what Gitcha can do with limited features.
                    </p>
                    <p className="price">Free</p>
                    <div className="btn-sec">
                      {userDetails?.PlanName == null ||
                      userType === USER_TYPE_MEMBER ? (
                        <Button
                          variant="primary"
                          className="plan-btn"
                          onClick={() => upgradeUserToFreePlan()}
                        >
                          Select
                        </Button>
                      ) : userDetails?.PlanName === "Lite" ? (
                        <Button variant="primary" className="plan-btn" disabled>
                          Your current plan
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          className="btn-outline-primary plan-btn"
                          onClick={handleDowngrade}
                        >
                          Downgrade
                        </Button>
                      )}
                    </div>
                    {!isBLS && (
                      <ul className="plan-features">
                        <li>Unlimited want-listings</li>
                        <li>Up to 2 properties in your inventory</li>
                        <li>Pay per introduction</li>
                      </ul>
                    )}
                    {isBLS && (
                      <p className="bls-lite-baseline">
                        Included with your {mlsShortName} seat
                      </p>
                    )}
                  </div>
                  <div className="plan-wraper premium w-100 pad-lr-21">
                    {userDetails?.PlanName != "Pro Member" && (
                      <span className="table-badge-green">Most popular</span>
                    )}
                    <h4 className="d-flex align-items-center">
                      Pro Member <span className="pro-blue-badge">PRO</span>
                    </h4>
                    <p className="info">{proMemberInfo}</p>
                    <p className="price">
                      $15/month<span>Billed monthly</span>
                    </p>
                    <div className="btn-sec">
                      {userDetails?.PlanName == null ? (
                        <Button
                          variant="primary"
                          className="plan-btn"
                          onClick={() => handleUpgrade(2, "Pro Member")}
                        >
                          Select
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          className="plan-btn"
                          disabled={
                            userDetails?.PlanName === "Pro Member"
                          }
                          onClick={() => handleUpgrade(2, "Pro Member")}
                        >
                          {userDetails?.PlanName === "Pro Member"
                            ? "Your Current Plan"
                            : isBLS
                              ? "Become a Gitcha Pro"
                              : "Upgrade to Pro Member"}
                        </Button>
                      )}
                    </div>
                    <ul className="plan-features">
                      {isBLS
                        ? blsProMemberFeatures.map((f) => (
                            <li key={f.label}>
                              <strong className="plan-features__label">
                                {f.label}
                              </strong>
                              <span className="plan-features__benefit">
                                {f.benefit}
                              </span>
                            </li>
                          ))
                        : (
                          <>
                            <li>Client management dashboard</li>
                            <li>Unlimited want-listings</li>
                            <li>Unlimited properties in your inventory</li>
                            <li>10 free introductions per month</li>
                            <li>Custom agent profile and Pro badge</li>
                            <li>Branded Buyer Digital Listing Form</li>
                            <li>See other Pro Member's contact info</li>
                          </>
                        )}
                    </ul>
                  </div>
                  {!isBLS && (
                    <div className="plan-wraper preferred w-100">
                      <h4 className="d-flex align-items-center">
                        Pro Preferred <span className="pro-gold-badge">PRO</span>
                      </h4>
                      <p className="info">
                        We’re working on new features that will be a game changer
                        for agents.
                      </p>
                      <p className="price">Coming soon</p>
                      <div className="btn-sec">
                        <Button
                          variant="outline"
                          className="btn-outline-blue plan-btn"
                        >
                          Notify Me
                        </Button>
                      </div>
                      <div className="plan-outer">
                        <ul className="plan-features">
                          <li>Unlimited want-listings</li>
                          <li>Custom agent profile and Premium badge</li>
                          <li>Unlimited properties in your inventory</li>
                          <li>10 free introductions per month</li>
                          <li>Client management dashboard </li>
                        </ul>
                      </div>
                    </div>
                  )}

                  {!isBLS && (
                    <div className="plan-wraper preferred w-100">
                      <h4>Enterprise</h4>
                      <p className="info">
                        Empowering your Agency's full team with Premium benefits.
                      </p>
                      <p className="price">Tiered pricing/seat</p>
                      <div className="btn-sec">
                        <Button
                          variant="primary"
                          className="plan-btn"
                          onClick={() => {
                            ContactModalShow();
                          }}
                        >
                          Contact us
                        </Button>
                      </div>
                      <ul className="plan-features">
                        <li>Unlimited want-listings</li>
                        <li>Custom agent profile and Premium badge</li>
                        <li>Unlimited properties in your inventory</li>
                        <li>Free introductions per month</li>
                        <li>Client management dashboard</li>
                        <li>
                          Exclusive market analytics, real-time buyer demand and
                          private inventory data reports
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>
          </div>
          )}
        </Modal.Body>
      </Modal>
      <PlanReviewModalWrapper
        period={selectedPeriod}
        setPeriod={setSelectedPeriod}
        onSignupPremiumAgentSuccess={onSignupPremiumAgentSuccess}
        selectedPlan={selectedPlan}
        show={showPlanReviewModal}
        onHide={PlanReviewModalClose}
        updateReloadParam={updateReloadParam}
        fromSignUp={fromSignUp}
        handleShowAgentModal={handleShowAgentModal}
        enablePromocode={enablePromocode}
        enablePromocodePeriod={enablePromocodePeriod}
        userDetails={userDetails}
        showPlanModal={props.showPlanModal}
        UpgradeToAgent={UpgradeToAgent}
        currentActivePlan={currentActivePlan}
        continueButtonClicked={continueButtonClicked}
        setContinueButtonClicked={setContinueButtonClicked}
      />
      {showCancelPlanModal && (
        <CancelPlanModal
          show={showCancelPlanModal}
          onHide={CancelPlanModalClose}
          updateReloadParam={updateReloadParam}
          userDetails={userDetails}
        />
      )}
      <PlanUpgradeRestrictionModal
        show={showPlanRestrictionModal}
        onHide={() => {
          setShowPlanRestrictionModal(false);
          setContinueButtonClicked(false);
        }}
        currentActivePlan={currentActivePlan}
        selectedPeriod={selectedPeriod}
        handleContinueButton={() => handleContinueClick()}
      />
      <ContactModal
        show={contactModalState}
        onHide={ContactModalClose}
        sourceType={2} // Enterprise
      />
      <ShowEditProfileModal
        editProfileModal={editProfileModal}
        EditProfileModalClose={EditProfileModalClose}
        userDetails={currentActivePlan}
        selectedPlan={selectedPlan}
      />
    </>
  );
};
export default PremiumPlanModal;
